import React from "react";
import { AlertTriangle, CheckCircle2, XCircle, Info } from "lucide-react";

type DialogType = "warning" | "success" | "error" | "info";

interface ConfirmDialogProps {
  open: boolean;
  type?: DialogType;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  children?: React.ReactNode;
}

const getIcon = (type: DialogType = "info", size = "w-16 h-16 mb-2") => {
  switch (type) {
    case "warning":
      return (
        <AlertTriangle
          className={`${size} text-yellow-500`}
          stroke="currentColor"
        />
      );
    case "success":
      return (
        <CheckCircle2
          className={`${size} text-green-500`}
          stroke="currentColor"
        />
      );
    case "error":
      return (
        <XCircle className={`${size} text-red-500`} stroke="currentColor" />
      );
    default:
      return <Info className={`${size} text-blue-500`} stroke="currentColor" />;
  }
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  type = "info",
  title = "",
  description,
  confirmText = "",
  cancelText = "",
  onConfirm,
  onCancel,
  isLoading = false,
  children,
}) => {
  if (!open) return null;

  return (
    <div className="fixed z-50 inset-0 flex items-center justify-center">
      <div
        className="fixed inset-0 bg-black bg-opacity-30"
        aria-hidden="true"
      />
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md mx-auto z-50 relative">
        <div className="flex flex-col items-center mb-6 gap-3">
          <div>{getIcon(type)}</div>
          <span className="text-2xl font-extrabold text-center">{title}</span>
        </div>
        {description && (
          <div className="mb-8 text-gray-700 text-center text-base">
            {description}
          </div>
        )}

        {children}
        <div className="flex justify-center gap-3 mt-6">
          <button
            onClick={onCancel}
            className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold text-base w-40"
          >
            {cancelText}
          </button>

          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
